// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ActionIcon, Box, Button, Center, CloseButton, Drawer, Grid, Loader, Stack, Tabs, Text, TextInput, Title } from "@mantine/core";
import useExternalApiService from "helpers/ExternalApiService";
import { FC, useEffect, useState } from "react";
import { ReactComponent as OppsIcon } from "assets/CurrencyCircleDollar.svg";
import { ReactComponent as RisksIcon } from "assets/Warning.svg";
import { ReactComponent as MagnifyingGlassIcon } from 'assets/magnifyingGlass.svg';
import { ReactComponent as FadersIcon } from "assets/faders.svg";
import { ReactComponent as SortAscIcon } from "assets/SortAscending.svg";
import { ReactComponent as SortDescIcon } from "assets/SortDescending.svg";

import List from "./components/List";
import FilterCarousel from "Modules/PractitionersList/components/FilterCarousel";
import FilterDropdown from "Modules/PractitionersList/components/FilterDropdown";
import { useDebouncedState } from "helpers/useDebouncedState";
import OpportunityForm from "components/OpportunityForm";
import ReactSelect from "react-select";
import { store } from 'stores/Store';
import AdvancedFilterForm from "./components/AdvancedFilterForm";

const OPP_FILTER = [
  "Opened",
  "Closed",
  "Prospect (Sales Qualified)",
  "Prepare/Qualify",
  "Present",
  "Follow-Up",
]

const RISK_FILTER = [
  "Closed Lost",
  "Closed Won",
  "Prospect (Sales Qualified)",
  "Prepare/Qualify",
  "Present",
  "Follow-Up",
]

//["Forecast Type", "forecast_type"],

const SEARCH_BY_FIELDS_MAP = new Map([
  ["Win Loss Reason", "win_loss_reason"],
  ["Product SKU", "ITEM_CODE"],
  ["Product Name", "ITEM_FULL_NAME"],
]);

const SORT_BY_FIELDS_MAP_OPP = new Map([
  ["Revenue Start Date", "expected_close_date"],
  ["Projected Revenue Total", "projected_total"],
  ["Product SKU", "ITEM_CODE"],
  ["Specialty Type", "specialty"],
  ["Practitioner", "name"]
]);

const SORT_BY_FIELDS_MAP_RISK = new Map([
  ["Revenue Risk Start Date", "expected_close_date"],
  ["Projected Revenue Risk Total", "projected_total"],
  ["Product SKU", "ITEM_CODE"],
  ["Specialty Type", "specialty"],
  ["Practitioner", "name"]
]);

interface AdvancedFilter {
  minExpectedCloseDate?: string;
  maxExpectedCloseDate?: string;
  minProjectedTotal?: number | string;
  maxProjectedTotal?: number | string;
  span?: number;
}

interface OppsAndRiskProps {
  initialTab?: "opps" | "risks";
  initialFilter?: string;
  startDate?: string;  // Date range filter from Funnel (format: YYYY-MM-DD)
  endDate?: string;    // Date range filter from Funnel (format: YYYY-MM-DD)
}

const OppsAndRisk: FC<OppsAndRiskProps> = ({
  initialTab = "opps",
  initialFilter = "All",
  startDate,
  endDate
}) => {
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [opps, setOpps] = useState([]);
  const [risks, setRisks] = useState([]);
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const [searchByFields, setSearchByFields] = useState<string[]>(
    [...SEARCH_BY_FIELDS_MAP.keys()]
  );
  const [isDropdownSelectOpen, setIsDropdownSelectOpen] = useState(false);
  const [debouncedSearchStr, searchStr, setSearchStr] = useDebouncedState("");
  const [viewOppOrRisk, setViewOppOrRisk] = useState<any | null>(null);
  const [sortByOpp, setSortByOpp] = useState("Revenue Start Date");
  const [sortByRisk, setSortByRisk] = useState("Revenue Risk Start Date");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [isAdvancedFilterOpened, setIsAdvancedFilterOpened] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState<AdvancedFilter>({
    minExpectedCloseDate: startDate || "",
    maxExpectedCloseDate: endDate || "",
    minProjectedTotal: "",
    maxProjectedTotal: "",
    span: 0,
  });
  const [showOnlyAtRisk, setShowOnlyAtRisk] = useState(false);

  const fetchOpps = () => {
    setIsLoading(true);
    let maxExpectedCloseDate = advancedFilter.maxExpectedCloseDate || "";
    if (advancedFilter?.span) {
      const currentYear = (new Date()).getFullYear();
      const currentMonth = (new Date()).getMonth();
      const monthSpan = advancedFilter.span / 30;
      maxExpectedCloseDate = `${currentYear}/${currentMonth + monthSpan + 1}/03`;
    }
    postData('/api/opportunities/all', {
      "fmc_email": store.impersonatedUser,
      "alias": store.impersonatedUserAlias,
      "is_risk": null,  // Include both at-risk and non-risk opportunities to match Funnel counts
      "filter": selectedFilter === "All" ? "Opened" : selectedFilter,
      "search_value": debouncedSearchStr,
      "search_by": searchByFields.map((f) => SEARCH_BY_FIELDS_MAP.get(f)),
      "sort_by": SORT_BY_FIELDS_MAP_OPP.get(sortByOpp) || "",
      "sort_order": sortOrder,
      "advanced_filter": {
        "min_expected_close_date": advancedFilter.minExpectedCloseDate || "",
        "max_expected_close_date": maxExpectedCloseDate,
        "min_projected_total": advancedFilter.minProjectedTotal,
        "max_projected_total": advancedFilter.maxProjectedTotal,
      }
    }).then((response) => {
      setOpps(response.opportunities);
    }).finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (activeTab === "opps") {
      fetchOpps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, debouncedSearchStr, activeTab, sortByOpp, advancedFilter, sortOrder]);

  const fetchRisks = () => {
    setIsLoading(true);
    let maxExpectedCloseDate = advancedFilter.maxExpectedCloseDate || "";
    if (advancedFilter?.span) {
      const currentYear = (new Date()).getFullYear();
      const currentMonth = (new Date()).getMonth();
      const monthSpan = advancedFilter.span / 30;
      maxExpectedCloseDate = `${currentYear}/${currentMonth + monthSpan + 1}/03`;
    }
    postData('/api/opportunities/all', {
      "fmc_email": store.impersonatedUser,
      "alias": store.impersonatedUserAlias,
      "is_risk": true,
      "filter": selectedFilter === "All At Risk" ? "Opened" : selectedFilter,
      "search_value": debouncedSearchStr,
      "search_by": searchByFields.map((f) => SEARCH_BY_FIELDS_MAP.get(f)),
      "sort_by": SORT_BY_FIELDS_MAP_RISK.get(sortByRisk) || "",
      "sort_order": sortOrder,
      "advanced_filter": {
        "min_expected_close_date": advancedFilter.minExpectedCloseDate || "",
        "max_expected_close_date": maxExpectedCloseDate,
        "min_projected_total": advancedFilter.minProjectedTotal,
        "max_projected_total": advancedFilter.maxProjectedTotal,
      }
    }).then((response) => {
      setRisks(response.opportunities);
    }).finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (activeTab === "risks") {
      fetchRisks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, debouncedSearchStr, activeTab, sortByRisk, advancedFilter, sortOrder]);

  return (
    <Box>
      <Drawer
        opened={!!viewOppOrRisk}
        zIndex={5001}
        size="xl"
        radius="30px 30px 0 0"
        onClose={() => {
          setViewOppOrRisk(null);
        }}
        position="bottom"
        styles={{
          header: {
            display: "none"
          }
        }}
      >
        <Box
          p={0}
          pt={40}
          w="100%"
          pos="relative">
          {viewOppOrRisk &&
            (<OpportunityForm
              action={viewOppOrRisk.id}
              target={viewOppOrRisk.is_risk ? "risk" : "opportunity"}
              onSave={() => {
                if (viewOppOrRisk.is_risk) {
                  fetchRisks();
                } else {
                  fetchOpps();
                }
                setViewOppOrRisk(null);
              }}
              currentPract={{
                netsuite_id: viewOppOrRisk.netsuite_id,
                id: viewOppOrRisk.practitioner_id,
                primary_name: viewOppOrRisk.practitioner_name,
              }} />)}
          <CloseButton
            size="xl"
            pos="absolute"
            top={10}
            right={-10}
            onClick={() => {
              setViewOppOrRisk(null);
            }}
          />
        </Box>
      </Drawer>

      <Drawer
        opened={isAdvancedFilterOpened}
        zIndex={5001}
        size="md"
        radius="30px 30px 0 0"
        onClose={() => {
          setIsAdvancedFilterOpened(false);
        }}
        position="bottom"
        styles={{
          header: {
            display: "none"
          }
        }}
      >
        <Box
          p={0}
          pt={40}
          w="100%"
          pos="relative">
          <AdvancedFilterForm
            initialValues={advancedFilter}
            onClear={(v) => setAdvancedFilter(v)}
            onClose={(a) => {
              setAdvancedFilter(a);
              setIsAdvancedFilterOpened(false);
            }} />
          <CloseButton
            size="xl"
            pos="absolute"
            top={10}
            right={-10}
            onClick={() => {
              setIsAdvancedFilterOpened(false);
            }}
          />
        </Box>
      </Drawer>

      <Title order={3} fw="700">Opportunities & Risks</Title>
      <Title order={5} c="dimmed" mb="sm">
        {activeTab === "opps" ? (
          <>{opps.length} opportunities</>
        ) : (
          <>{risks.length} risks</>
        )}
      </Title>
      <Tabs value={activeTab} onChange={(t) => {
        setActiveTab(t);
        setSearchStr("");
        setSelectedFilter("All");
        setAdvancedFilter({
          minExpectedCloseDate: "",
          maxExpectedCloseDate: "",
          minProjectedTotal: "",
          maxProjectedTotal: "",
          span: 0,
        })
      }}
        color="#4F7D9C"
      >
        <Tabs.List>
          <Tabs.Tab value="opps">
            <Stack align="center" c={activeTab === "opps" ? "#4F7D9C" : "dimmed"}>
              <OppsIcon style={{ transform: "scale(1.0)" }} />
              <Text mt={-10} fz="xs" fw="bold">Opportunities</Text>
            </Stack>
          </Tabs.Tab>
          <Tabs.Tab value="risks">
            <Stack align="center" c={activeTab === "risks" ? "#4F7D9C" : "dimmed"}>
              <RisksIcon style={{ transform: "scale(1.0)" }} />
              <Text mt={-10} fz="xs" fw="bold">Risks</Text>
            </Stack>
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="opps">
          <Box
            mx="xs"
            pos="relative"
            mih={370}
            style={{
              overflowX: "hidden",

            }}>
            <Box pos="relative">
              <TextInput
                value={searchStr}
                onChange={(e) => setSearchStr(e.currentTarget.value)}
                my={0}
                variant="unstyled"
                style={{
                  borderBottom: "1px solid #ccc",
                  paddingTop: 2,
                  paddingBottom: 2,
                  fontSize: 16,
                }}
                styles={{
                  input: {
                    fontSize: 16,
                    paddingTop: 2,
                    paddingBottom: 2,
                    '::placeholder': { fontSize: 16 },
                    '::-webkit-input-placeholder': { fontSize: 16 },
                  },
                }}
                leftSection={<MagnifyingGlassIcon height={20} />}
                rightSection={<FadersIcon
                  height={20}
                  onClick={() => {
                    setIsDropdownSelectOpen(true);
                  }}
                />}
                placeholder="Search" />
              <FilterDropdown
                isOpen={isDropdownSelectOpen}
                selectedValues={searchByFields}
                onValueChange={(v) => setSearchByFields(v)}
                options={[...SEARCH_BY_FIELDS_MAP.keys()]}
                onClose={() => {
                  setIsDropdownSelectOpen(false);
                }}
                actionButton={(
                  <Text
                    ta="center"
                    c="#4F7D9C"
                    pt="xs"
                    fw="bold"
                    style={{
                      borderTop: "1px solid #ccc"
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAdvancedFilterOpened(true);
                      setIsDropdownSelectOpen(false);
                    }}>
                    Advanced Filter
                  </Text>
                )}
              />
            </Box>
            <FilterCarousel
              filterList={["All", ...OPP_FILTER]}
              selectedFilter={selectedFilter}
              onFilterChange={(f) => setSelectedFilter(f)}
            />
            <Box mt={0} mb="xs">
              <Button
                variant="filled"
                color={showOnlyAtRisk ? "green" : "red"}
                size="xs"
                onClick={() => setShowOnlyAtRisk(!showOnlyAtRisk)}
              >
                {showOnlyAtRisk ? "Show all opportunities" : "Show only At-Risk"}
              </Button>
            </Box>
            <Grid
              grow
              align="center"
              mt="xs">
              <Grid.Col span={1.4}>
                <Text c="dimmed">Sort By</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <ReactSelect
                  value={{
                    value: sortByOpp,
                    label: sortByOpp,
                  }}
                  options={[...SORT_BY_FIELDS_MAP_OPP.keys()].map((f) => ({
                    label: f,
                    value: f,
                  }))}
                  onChange={(v) => {
                    if (v) {
                      setSortByOpp(v.value);
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={0.5}>
                {sortOrder === "ASC" ? (
                  <SortAscIcon onClick={() => setSortOrder("DESC")} />
                ) : (
                  <SortDescIcon onClick={() => setSortOrder("ASC")} />
                )}
              </Grid.Col>
            </Grid>
            {isLoading ? (
              <Center mt="xl">
                <Loader />
              </Center>
            ) : (
              <List
                list={showOnlyAtRisk ? opps.filter((o: any) => o.is_risk) : opps}
                isRisk={false}
                onClick={(opp) => {
                  setViewOppOrRisk(opp);
                }}
              />
            )}
          </Box>
        </Tabs.Panel>
        <Tabs.Panel value="risks">
          <Box
            mx="xs"
            pos="relative"
            mih={370}
            style={{
              overflowX: "hidden",
            }}>
            <Box pos="relative">
              <TextInput
                value={searchStr}
                onChange={(e) => setSearchStr(e.currentTarget.value)}
                my={0}
                variant="unstyled"
                style={{
                  borderBottom: "1px solid #ccc",
                  paddingTop: 2,
                  paddingBottom: 2,
                  fontSize: 16,
                }}
                styles={{
                  input: {
                    fontSize: 16,
                    paddingTop: 2,
                    paddingBottom: 2,
                    '::placeholder': { fontSize: 16 },
                    '::-webkit-input-placeholder': { fontSize: 16 },
                  },
                }}
                leftSection={<MagnifyingGlassIcon height={20} />}
                rightSection={<FadersIcon
                  height={20}
                  onClick={() => {
                    setIsDropdownSelectOpen(true);
                  }}
                />}
                placeholder="Search" />
              <FilterDropdown
                isOpen={isDropdownSelectOpen}
                selectedValues={searchByFields}
                onValueChange={(v) => setSearchByFields(v)}
                options={[...SEARCH_BY_FIELDS_MAP.keys()]}
                onClose={() => {
                  setIsDropdownSelectOpen(false);
                }}
                actionButton={(
                  <Text
                    ta="center"
                    c="#4F7D9C"
                    pt="xs"
                    fw="bold"
                    style={{
                      borderTop: "1px solid #ccc"
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAdvancedFilterOpened(true);
                      setIsDropdownSelectOpen(false);
                    }}>
                    Advanced Filter
                  </Text>
                )}
              />
            </Box>
            <FilterCarousel
              filterList={["All At Risk", ...RISK_FILTER]}
              selectedFilter={selectedFilter}
              onFilterChange={(f) => setSelectedFilter(f)}
            />
            <Grid
              grow
              align="center"
              mt="0">
              <Grid.Col span={1.4}>
                <Text c="dimmed">Sort By</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <ReactSelect
                  value={{
                    value: sortByRisk,
                    label: sortByRisk,
                  }}
                  options={[...SORT_BY_FIELDS_MAP_RISK.keys()].map((f) => ({
                    label: f,
                    value: f,
                  }))}
                  onChange={(v) => {
                    if (v) {
                      setSortByRisk(v.value);
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={0.5}>
                {sortOrder === "ASC" ? (
                  <SortAscIcon onClick={() => setSortOrder("DESC")} />
                ) : (
                  <SortDescIcon onClick={() => setSortOrder("ASC")} />
                )}
              </Grid.Col>
            </Grid>

            {isLoading ? (
              <Center mt="xl">
                <Loader />
              </Center>
            ) : (
              <List
                list={risks}
                isRisk={true}
                onClick={(risk) => {
                  setViewOppOrRisk(risk);
                }}
              />
            )}
          </Box>
        </Tabs.Panel>
      </Tabs>
    </Box>
  )
}
export default OppsAndRisk;